import { h, setChildren } from "../core/dom.js";
import { loadLibrary, muscles, muscleName } from "../store.js";
import { openModal, spinner } from "./ui.js";
import { exerciseThumb } from "./common.js";
import { openExerciseDetail } from "./exerciseDetailModal.js";
import { icon } from "../lib/icons.js";

/**
 * Open the exercise picker modal.
 *   onPick(exercise) -> truthy if it was added (false if it was a duplicate).
 *   multi    : keep the modal open and track a running count.
 *   existing : array of library_exercise_ids already in the workout (shown as Added).
 */
export function openExercisePicker({ onPick, multi = false, title = "Add exercise", existing = [] } = {}) {
  const body = h("div", { class: "picker" }, spinner());
  const modal = openModal({ title, content: body, fullscreen: true });
  let library = [];
  const added = new Set(existing);
  const filters = { q: "", muscle: "", equipment: "", level: "" };

  loadLibrary()
    .then((lib) => {
      library = lib;
      setChildren(body, view());
      renderList();
    })
    .catch(() => {
      // Offline with no cached catalog: don't hang on the spinner.
      setChildren(body, h("div", { class: "dim center", style: { padding: "30px 10px" } }, "Exercise list unavailable offline. Reconnect to add an exercise."));
    });

  const equipments = () => [...new Set(library.map((e) => e.equipment).filter(Boolean))].sort();

  function view() {
    return h(
      "div",
      null,
      h(
        "div",
        { class: "hstack", style: { gap: "6px", marginBottom: "10px" } },
        icon("search", { size: 18, className: "icon muted" }),
        h("input", { class: "input", placeholder: "Search exercises…", oninput: (e) => { filters.q = e.target.value.toLowerCase(); renderList(); } })
      ),
      h(
        "div",
        { class: "chip-row", style: { marginBottom: "10px" } },
        sel("muscle", "All muscles", muscles().map((m) => [m.muscle_id, m.muscle])),
        sel("equipment", "All equipment", equipments().map((e) => [e, e])),
        sel("level", "All levels", [["beginner", "Beginner"], ["intermediate", "Intermediate"], ["expert", "Expert"]])
      ),
      multi ? h("div", { class: "dim", id: "picker-count", style: { marginBottom: "8px" } }, countLabel()) : null,
      h("div", { id: "picker-list", class: "ex-list" })
    );
  }

  function countLabel() {
    return `${added.size} added`;
  }

  function sel(key, label, options) {
    return h(
      "select",
      { class: "select", style: { flex: "1 1 30%", minWidth: "110px", padding: "8px 10px" }, onchange: (e) => { filters[key] = e.target.value; renderList(); } },
      h("option", { value: "" }, label),
      ...options.map(([v, l]) => h("option", { value: v }, l))
    );
  }

  function filtered() {
    return library.filter((ex) => {
      if (filters.q && !ex.exercise_name.toLowerCase().includes(filters.q)) return false;
      if (filters.equipment && ex.equipment !== filters.equipment) return false;
      if (filters.level && ex.level !== filters.level) return false;
      if (filters.muscle) {
        const id = Number(filters.muscle);
        if (![...(ex.primary_muscle_ids || []), ...(ex.secondary_muscle_ids || [])].includes(id)) return false;
      }
      return true;
    });
  }

  function row(ex) {
    const isAdded = added.has(ex.id);
    const addBtn = h(
      "button",
      {
        class: ["icon-btn", isAdded ? "" : "ghost"],
        style: isAdded ? { background: "var(--success)", color: "#fff" } : null,
        title: isAdded ? "Added" : "Add",
        onClick: (e) => { e.stopPropagation(); add(ex); },
      },
      icon(isAdded ? "check" : "plus", { size: 20 })
    );
    return h(
      "div",
      { class: "ex-row", dataset: { id: ex.id } },
      h(
        "div",
        { class: "hstack grow card-tap", style: { minWidth: 0, gap: "12px" }, onClick: () => openExerciseDetail(ex.id) },
        exerciseThumb(ex.images),
        h(
          "div",
          { class: "ex-info" },
          h("div", { class: "ex-name" }, ex.exercise_name),
          h("div", { class: "ex-meta" }, [ex.equipment, (ex.primary_muscle_ids || []).map(muscleName).filter(Boolean)[0]].filter(Boolean).join(" · "))
        )
      ),
      h("button", { class: "icon-btn ghost", title: "Details", "aria-label": `View details for ${ex.exercise_name}`, onClick: (e) => { e.stopPropagation(); openExerciseDetail(ex.id); } }, icon("info", { size: 18 })),
      addBtn
    );
  }

  function renderList() {
    const list = body.querySelector("#picker-list");
    if (!list) return;
    const items = filtered().slice(0, 200);
    if (!items.length) {
      setChildren(list, h("p", { class: "dim center", style: { padding: "30px" } }, "No matches"));
      return;
    }
    setChildren(list, ...items.map(row));
  }

  function refreshRow(ex) {
    const el = body.querySelector(`.ex-row[data-id="${ex.id}"]`);
    if (el) el.replaceWith(row(ex));
    const c = body.querySelector("#picker-count");
    if (c) c.textContent = countLabel();
  }

  function add(ex) {
    if (multi) {
      if (added.has(ex.id)) return; // already in the workout
      const result = onPick?.(ex);
      added.add(ex.id); // mark added (result may be false if it was already present)
      refreshRow(ex);
    } else {
      onPick?.(ex);
      modal.close();
    }
  }

  return modal;
}
